import { Component, Q } from '@fullstory/tomato';
import { RankIndicatorView } from '@fullstory/tomato-views/views';
import { ToolTip, ToolTipColor } from '@fullstory/tomato-components';
import { Point } from '@fullstory/playback-mobile';
import { INDICATOR_SIZE } from './overlay';
import { IsRectDifferent } from './rectangles';
import { ElemStats } from '../page-insights/activitymaps';
import { maxHighlightedElements } from '../page-insights/activitymapsconst';
import { ComputeElementStatsRequest } from '@fullstory/proto/fs/services/alexa';
import { ToClientClickType } from '@fullstory/client-api';
import { ClickMapDisplayOptions } from '@fullstory/page-insights-ui';
import { SimpleRect } from '@fullstory/util/rect';

const SELECTED_BORDER_WIDTH = 3;
const BORDER_WIDTH = 1;
const heatmapClass = 'heatmap';
const selectedClass = 'selected';
const hoverClass = 'hover';

// A numbered indicator for a highlighted element that shows the order it falls in within a list.
export class RankIndicator extends Component<RankIndicatorView> {
  /** A div containing boxes around the top element and potentially related elements. */
  private _boxBordersContainer: Q.OfOne;
  /** The border box elements. [0] = top element and [1+] will be related elements. */
  private _relatedElementBorderBoxes: Q.OfOne[] = [];
  /** Holds the positions of the related playback elements' locations that we need to sync up with, if this is being displayed. */
  private _playbackElementPositions: SimpleRect[];
  private _lastIndicatorR: SimpleRect; // Keep track of the most recent position of the highlighted element.
  private _lastInverseScale = 1;
  private _lastTranslateY = 0;
  private _tooltip: ToolTip;
  private _borderWidth = BORDER_WIDTH;
  private _rank: number = -1;
  private _showRelatedElements: boolean = false;
  private _indicatorTooltipMarginX = 180;
  private _indicatorTooltipMarginY = 80;

  public onClick: () => void; //

  constructor(
    private _container: Q.OfOne,
    private _elemStats: ElemStats,
    private _displayOptions: ClickMapDisplayOptions,
    private _classes?: string[],
  ) {
    super(new RankIndicatorView());

    this.view().on('click', () => {
      if (this.onClick) {
        this.onClick();
      }
    });

    this._boxBordersContainer = Q.createOne('div').addClass('rects');
    const topElementBorderBox = this.createBorderBox(false, false);

    if (_displayOptions.enableMouseOverHighlights) {
      topElementBorderBox.addClass('clickable');
    }
    if (this._displayOptions.heatmapMode) {
      this.view().addClass(heatmapClass);
    } else {
      const clickType = ToClientClickType.get(this._elemStats.Type);
      if (clickType) {
        this.view().addClass(clickType);
      }
    }
    this._container.append(this._boxBordersContainer);
    this._container.append(this.view());
    if (this._classes) {
      this._classes.forEach(c => {
        this.view().addClass(c);
      });
    }
  }

  private createBorderBox(relatedElement: boolean, isSelected: boolean): Q.OfOne {
    const relatedElementBorderBox = Q.createOne('div').addClass('rect');
    this._boxBordersContainer.append(relatedElementBorderBox);
    this._relatedElementBorderBoxes.push(relatedElementBorderBox);

    this._classes?.forEach(c => {
      if (c !== hoverClass) {
        relatedElementBorderBox.addClass(c);
      }
    });
    if (isSelected) {
      relatedElementBorderBox.addClass(selectedClass);
    }
    if (relatedElement) {
      relatedElementBorderBox.addClass('related-element');
    }

    if (this._displayOptions.heatmapMode) {
      relatedElementBorderBox.addClass('heatmap');
    } else {
      const clickType = ToClientClickType.get(this._elemStats.Type);
      if (clickType) {
        relatedElementBorderBox.addClass(clickType);
      }
    }

    return relatedElementBorderBox;
  }

  private generateToolTipContent(): Q.OfOne {
    const container = Q.createOne('div');
    for (const stat of this._elemStats.Stats) {
      container.append(Q.createOne('div').setText(stat.Val));
    }
    if (this._elemStats.Footnote) {
      if (this._displayOptions.allowHighlightRelatedElements) {
        const footnoteActionElement = Q.createOne('a');
        footnoteActionElement.e.addEventListener('click', () => this.showPossibleElements());

        container.append(
          Q.createOne('div')
            .setCss('display', 'flex')
            .append(
              footnoteActionElement.append(Q.createOne('div').setText(this._elemStats.Footnote)),
            ),
        );
      } else {
        container.append(
          Q.createOne('div')
            .setCss('display', 'flex')
            .append(Q.createOne('div').setText(this._elemStats.Footnote)),
        );
      }
    }
    return container;
  }

  private showPossibleElements() {
    if (this._showRelatedElements) {
      // Try to avoid expensive operations
      return;
    }

    this._showRelatedElements = true;
    this.setNumBorderBoxes(this._playbackElementPositions.length, true);
    this._positionBounds(this._playbackElementPositions);
  }

  private addClass(clazz: string) {
    this.view().addClass(clazz);
    this._relatedElementBorderBoxes.forEach(item => item.addClass(clazz));
    return this;
  }

  private removeClass(clazz: string) {
    this.view().removeClass(clazz);
    this._relatedElementBorderBoxes.forEach(item => item.removeClass(clazz));
  }

  private toolTip(): ToolTip {
    if (!this._tooltip) {
      const tipContents = this.generateToolTipContent();

      let color: ToolTipColor;
      if (this._displayOptions.heatmapMode) {
        color = ToolTipColor.Black;
      } else {
        switch (this._elemStats.Type) {
          case ComputeElementStatsRequest.StatsType.STATS_TYPE_RAGE_CLICKS:
            color = ToolTipColor.Green;
            break;
          case ComputeElementStatsRequest.StatsType.STATS_TYPE_DEAD_CLICKS:
            color = ToolTipColor.Yellow;
            break;
          case ComputeElementStatsRequest.StatsType.STATS_TYPE_ERROR_CLICKS:
            color = ToolTipColor.Red;
            break;
          default:
            color = ToolTipColor.Blue;
            break;
        }
      }

      this._tooltip = new ToolTip(
        this.view(),
        tipContents,
        { Color: color },
        { x: this._indicatorTooltipMarginX, y: this._indicatorTooltipMarginY },
      ).setPositionElem(this.getTopMostElementBox());
      if (this._displayOptions.heatmapMode) {
        this._tooltip.view().addClass('heatmap');
      }
    }
    return this._tooltip;
  }

  setHover(hover: boolean) {
    if (hover) {
      this._borderWidth = SELECTED_BORDER_WIDTH * 2;
      this.getTopMostElementBox().addClass(hoverClass);
      this.addClass(hoverClass);
    } else {
      this.getTopMostElementBox().removeClass(hoverClass);
      this.removeClass(hoverClass);
      if (this.view().hasClass(selectedClass)) {
        this._borderWidth = SELECTED_BORDER_WIDTH;
      } else {
        this._borderWidth = BORDER_WIDTH;
      }
    }
    this._positionBounds(this._playbackElementPositions);
  }

  setSelected(selected: boolean) {
    if (selected) {
      this.addClass(selectedClass);
      this._borderWidth = SELECTED_BORDER_WIDTH;
      this.toolTip().show();
    } else {
      this.setNumBorderBoxes(1, false);
      this._showRelatedElements = false;
      this.removeClass(selectedClass);
      this._borderWidth = BORDER_WIDTH;
      this.toolTip().hide();
    }
    this._positionBounds(this._playbackElementPositions);
  }

  getInitialCentroid(elemRect: SimpleRect, inverseScale: number): Point {
    const scaledSize = this.getScaledSize(inverseScale);
    return <Point>{
      x: elemRect.left - scaledSize / 2,
      y: elemRect.top - scaledSize / 2,
    };
  }

  getScaledSize(inverseScale: number): number {
    return INDICATOR_SIZE * inverseScale;
  }

  /**
   * Takes in the SimpleRect for the element we will highlight, and the desired position
   *
   * @param elemRects Array of `ClientRects`. The first one will have the indicator applied to it while any additional
   * ones will be related and have select and hover overlays applied to them.
   */
  position(elemRects: SimpleRect[], indicatorRect: SimpleRect, animate: boolean = true) {
    if (this._tooltip) {
      setTimeout(() => {
        if (this._tooltip) {
          this._tooltip.reposition();
        }
      });
    }
    if (
      this._playbackElementPositions &&
      !IsRectDifferent(this._playbackElementPositions[0], elemRects[0])
    ) {
      return;
    }

    this._playbackElementPositions = elemRects;
    this._lastIndicatorR = indicatorRect;

    // Position and size bounding box.
    this._positionBounds(elemRects);

    // Position indicator.
    this.positionIndicator(indicatorRect, animate);
  }

  /**
   * Adds or removes border boxes depending on how many are needed for the top element (required) + related elements (optional).
   */
  private setNumBorderBoxes(numBoxes: number, isSelected: boolean) {
    const numToChange = numBoxes - this._relatedElementBorderBoxes.length;

    if (numToChange > 0) {
      for (let numToCreate = 0; numToCreate < numToChange; numToCreate++) {
        this.createBorderBox(true, isSelected);
      }
    } else if (numToChange < 0) {
      const qItemsToRemove = this._relatedElementBorderBoxes.splice(numToChange);
      qItemsToRemove.forEach(item => {
        item.remove();
      });
    }
  }

  lastIndicatorRect(): SimpleRect {
    return this._lastIndicatorR;
  }

  setRank(idx: number, mapIdx: number): RankIndicator {
    this.view().content.setText(`${idx + 1}`); // 1's based
    this._rank = idx + 1;
    if (idx < maxHighlightedElements) {
      this._relatedElementBorderBoxes.forEach(item => item.addClass(`map-color-${mapIdx}`));
    } else {
      this.view().remove();
      this._relatedElementBorderBoxes.forEach(item => item.remove());
      this._boxBordersContainer.remove();
    }
    return this;
  }

  get rank(): number {
    return this._rank;
  }

  /**
   * Returns the area of the engagement box if the given mouse event is within the bounding box. If not, this will return
   * 0 as the mouse event isn't related to this RankIndicator's element.
   */
  getRelatedElementArea(e: MouseEvent): number {
    const boundingClientRect = this.getTopMostElementBox().e.getBoundingClientRect();
    const offsetTop = boundingClientRect.top;
    const offsetLeft = boundingClientRect.left;
    const x = e.x;
    const y = e.y;
    if (
      y >= offsetTop &&
      y <= offsetTop + boundingClientRect.height &&
      x >= offsetLeft &&
      x <= offsetLeft + boundingClientRect.width
    ) {
      return boundingClientRect.height * boundingClientRect.width;
    }

    return 0;
  }

  enabledBoxPointerEvents() {
    this.getTopMostElementBox().e.style.pointerEvents = 'all';
  }

  disableBoxPointerEvents() {
    this.getTopMostElementBox().e.style.pointerEvents = 'none';
  }

  setBoxMouseAction({
    onMouseMove,
    onMouseOut,
    onClick,
    onWheel,
  }: {
    onMouseMove: (e: MouseEvent) => void;
    onMouseOut: (e: MouseEvent) => void;
    onClick: (e: MouseEvent) => void;
    onWheel: (e: WheelEvent) => void;
  }) {
    const mainBorderBox = this.getTopMostElementBox();
    mainBorderBox.e.addEventListener('mousemove', onMouseMove);
    mainBorderBox.e.addEventListener('mouseout', onMouseOut);
    mainBorderBox.e.addEventListener('click', onClick);
    mainBorderBox.e.addEventListener('wheel', onWheel);
  }

  onResize(inverseScale: number) {
    this._setTransform(this._lastTranslateY, inverseScale);
    this._positionBounds(this._playbackElementPositions);
  }

  positionIndicator(indicatorRect: SimpleRect, animate: boolean = true) {
    this.view().setCss('left', `${indicatorRect.left}px`);
    this.view().setCss('top', `${indicatorRect.top}px`);
    this.view().setCss('display', 'inline-block');

    if (animate) {
      this.view().addClass('animate');
      this._setTransform(-(INDICATOR_SIZE * 2), this._lastInverseScale);

      // Animate in.
      setTimeout(() => {
        this.view().addClass('is-on');
        this._setTransform(0, this._lastInverseScale);
      }, 10);
    } else {
      this.view().addClass('is-on');
    }
  }

  _setTransform(translateY: number, inverseScale: number) {
    this._lastTranslateY = translateY;
    this._lastInverseScale = inverseScale;
    this.view().setCss(
      'transform',
      `translateY(${translateY}px) scale(${inverseScale},${inverseScale})`,
    );
  }

  _positionBounds(elemRects: SimpleRect[]) {
    this._relatedElementBorderBoxes.forEach((borderBox, index) => {
      const elemRect = elemRects[index];
      borderBox
        .setCss('left', `${elemRect.left - this._borderWidth}px`)
        .setCss('top', `${elemRect.top - this._borderWidth}px`)
        .setCss('width', `${elemRect.width + 2 * this._borderWidth}px`)
        .setCss('height', `${elemRect.height + 2 * this._borderWidth}px`);
      borderBox.setCss('border-width', `${this._borderWidth * this._lastInverseScale}px`);
    });
  }

  // Disables transition styles on the rank indicator and associated bounding box.
  disableTransition() {
    this.view().removeClass('animate');
  }

  dispose() {
    const t = this._tooltip;
    if (t) {
      t.hide(() => {
        t.view().remove();
      });
    }
    if (this._boxBordersContainer) {
      this._boxBordersContainer.remove();
    }
    this.view().remove();
  }

  private getTopMostElementBox(): Q.OfOne {
    return this._relatedElementBorderBoxes[0];
  }
}
